import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { AudioBus, playUIClick } from '../engine/audio';
import type { SkinDefinition } from '../config/skins';
import { WalletStore } from '../stores/WalletStore';

export type SkinCardState = 'equipped' | 'owned' | 'affordable' | 'locked';

export interface SkinCardOptions {
  x: number;
  y: number;
  skin: SkinDefinition;
  onAction: (skin: SkinDefinition, action: 'buy' | 'equip') => void;
  audio?: AudioBus;
}

/**
 * SkinCard — a single shop tile showing a skin's preview head, name, price,
 * and a context-aware action button (BUY / EQUIP / EQUIPPED / NEED N MORE).
 * Uses inner `visual` Container so the outer transform is free for hover tween.
 */
export class SkinCard extends Container {
  private visual: Container;
  private frame: Sprite;
  private equippedFrame: Sprite;
  private preview: Sprite;
  private nameText: Text;
  private priceGroup: Container;
  private priceText: Text;
  private coinIcon: Sprite;
  private actionBg: Graphics;
  private actionLabel: Text;
  private skin: SkinDefinition;
  private state: SkinCardState = 'locked';
  private onAction: SkinCardOptions['onAction'];
  private audio?: AudioBus;

  constructor(opts: SkinCardOptions) {
    super();
    this.position.set(opts.x, opts.y);
    this.skin = opts.skin;
    this.onAction = opts.onAction;
    this.audio = opts.audio;

    this.visual = new Container();
    this.visual.scale.set(1);
    this.addChild(this.visual);

    // Base card frame
    this.frame = new Sprite(Texture.from('ui_shop_card_bg'));
    this.frame.anchor.set(0.5);
    this.visual.addChild(this.frame);

    // Equipped overlay (gold glow + EQUIPPED ribbon). Shown only when equipped.
    this.equippedFrame = new Sprite(Texture.from('ui_shop_card_equipped'));
    this.equippedFrame.anchor.set(0.5);
    this.equippedFrame.visible = false;
    this.visual.addChild(this.equippedFrame);

    // Skin preview — the idle head, centered in the upper well of the card.
    this.preview = new Sprite(Texture.from(`${opts.skin.id}_head_idle`));
    this.preview.anchor.set(0.5);
    this.preview.position.set(0, -60);
    this.preview.scale.set(0.85);
    this.visual.addChild(this.preview);

    // Name banner text (sits over the top banner strip in the frame)
    this.nameText = new Text({
      text: opts.skin.displayName.toUpperCase(),
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 24,
        fill: 0xffd23f,
        stroke: { color: 0x1a0d22, width: 3 },
        letterSpacing: 2,
      },
    });
    this.nameText.anchor.set(0.5);
    this.nameText.position.set(0, -158);
    this.visual.addChild(this.nameText);

    // Price group (coin icon + number) — hidden when the skin is built-in
    this.priceGroup = new Container();
    this.priceGroup.position.set(0, 65);
    this.visual.addChild(this.priceGroup);

    this.coinIcon = new Sprite(Texture.from('ui_coin'));
    this.coinIcon.anchor.set(1, 0.5);
    this.coinIcon.scale.set(0.8);
    this.coinIcon.position.set(-6, 0);
    this.priceGroup.addChild(this.coinIcon);

    this.priceText = new Text({
      text: opts.skin.price.toLocaleString('en-US'),
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 32,
        fill: 0xffd23f,
        stroke: { color: 0x1a0d22, width: 3 },
      },
    });
    this.priceText.anchor.set(0, 0.5);
    this.priceText.position.set(4, 0);
    this.priceGroup.addChild(this.priceText);

    // Action button (pill under the price)
    this.actionBg = new Graphics();
    this.actionBg.position.set(0, 130);
    this.visual.addChild(this.actionBg);

    this.actionLabel = new Text({
      text: 'BUY',
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 22,
        fill: 0xf5e9d6,
        stroke: { color: 0x1a0d22, width: 2 },
      },
    });
    this.actionLabel.anchor.set(0.5);
    this.actionLabel.position.set(0, 130);
    this.visual.addChild(this.actionLabel);

    // Card-level hit area & hover animation
    this.eventMode = 'static';
    this.cursor = 'pointer';
    const w = this.frame.width;
    const h = this.frame.height;
    this.hitArea = {
      contains: (x: number, y: number): boolean =>
        x >= -w / 2 && x <= w / 2 && y >= -h / 2 && y <= h / 2,
    };

    const baseY = opts.y;
    this.on('pointerover', () => {
      gsap.to(this, { y: baseY - 8, duration: 0.16, ease: 'sine.out' });
      gsap.to(this.visual.scale, { x: 1.04, y: 1.04, duration: 0.16, ease: 'sine.out' });
    });
    this.on('pointerout', () => {
      gsap.to(this, { y: baseY, duration: 0.16, ease: 'sine.out' });
      gsap.to(this.visual.scale, { x: 1.0, y: 1.0, duration: 0.16, ease: 'sine.out' });
    });
    this.on('pointerdown', () => this.handleClick());

    this.refresh();
  }

  /** Re-evaluate state from WalletStore and redraw the card. */
  refresh(): void {
    const wallet = WalletStore.read();
    if (wallet.selectedSkin === this.skin.id) {
      this.state = 'equipped';
    } else if (wallet.ownedSkins.includes(this.skin.id)) {
      this.state = 'owned';
    } else if (wallet.coins >= this.skin.price) {
      this.state = 'affordable';
    } else {
      this.state = 'locked';
    }

    const owned = this.state === 'equipped' || this.state === 'owned';
    this.priceGroup.visible = !owned;
    this.equippedFrame.visible = this.state === 'equipped';

    const w = this.frame.width;
    const btnW = w * 0.72;
    const btnH = 52;
    this.actionBg.clear();
    let bgColor = 0x5fdbcf;
    let strokeColor = 0x1a0d22;
    let textColor = 0xf5e9d6;
    let labelStr = '';
    switch (this.state) {
      case 'equipped':
        bgColor = 0xffd23f;
        strokeColor = 0x7a5a08;
        textColor = 0x1a0d22;
        labelStr = 'EQUIPPED';
        break;
      case 'owned':
        bgColor = 0x5fdbcf;
        strokeColor = 0x1a0d22;
        textColor = 0x1a0d22;
        labelStr = 'EQUIP';
        break;
      case 'affordable':
        bgColor = 0xd94aa0;
        strokeColor = 0x1a0d22;
        textColor = 0xffffff;
        labelStr = `BUY`;
        break;
      case 'locked': {
        const missing = this.skin.price - wallet.coins;
        bgColor = 0x44424a;
        strokeColor = 0x1a0d22;
        textColor = 0xb0acba;
        labelStr = `NEED ${missing.toLocaleString('en-US')}`;
        break;
      }
    }
    this.actionBg
      .roundRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH / 2)
      .fill({ color: bgColor })
      .stroke({ color: strokeColor, width: 3 });
    this.actionLabel.text = labelStr;
    this.actionLabel.style.fill = textColor;

    // Pulse the equipped frame softly.
    if (this.state === 'equipped') {
      gsap.killTweensOf(this.equippedFrame);
      this.equippedFrame.alpha = 1;
      gsap.to(this.equippedFrame, {
        alpha: 0.75,
        duration: 1.2,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
    } else {
      gsap.killTweensOf(this.equippedFrame);
      this.equippedFrame.alpha = 1;
    }
  }

  private handleClick(): void {
    playUIClick(this.audio ?? null);
    if (this.state === 'equipped') {
      // Already selected — small bounce for feedback, no state change.
      gsap.fromTo(
        this.visual.scale,
        { x: 1.04, y: 1.04 },
        { x: 0.98, y: 0.98, duration: 0.08, yoyo: true, repeat: 1 },
      );
      return;
    }
    if (this.state === 'owned') {
      this.onAction(this.skin, 'equip');
      return;
    }
    if (this.state === 'affordable') {
      this.onAction(this.skin, 'buy');
      return;
    }
    // Locked — shake
    const baseX = this.x;
    gsap.to(this, {
      x: baseX + 8,
      duration: 0.05,
      yoyo: true,
      repeat: 5,
      ease: 'sine.inOut',
      onComplete: () => { this.x = baseX; },
    });
  }
}
